import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Popover, Typography } from '@mui/material';

import { Cloud, CloudCheck, CloudOff, Loader2 } from 'lucide-react';

import { useAppSelector } from '../../hooks';
import { green01, orangeError } from '../../theme';

/**
 * Drive-style autosave indicator for the workspace navbar.
 * Two primary states ("Saved to cloud" / "Saving changes…") plus an error state.
 * Click opens a short explanation that Taman autosaves every 5 seconds.
 */
export function AutosaveStatusIndicator() {
  const { t } = useTranslation();
  const status = useAppSelector((state) => state.workspace.autosaveStatus);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const label =
    status === 'saving'
      ? t('workspace.autosave.saving')
      : status === 'error'
        ? t('workspace.autosave.error')
        : t('workspace.autosave.saved');

  const iconColor = status === 'error' ? orangeError : status === 'saving' ? 'rgba(255, 255, 255, 0.7)' : green01;

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={label}
        aria-haspopup="true"
        aria-expanded={open}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          alignSelf: 'center',
          ml: 1.5,
          p: 0.75,
          border: 'none',
          borderRadius: '6px',
          background: 'transparent',
          cursor: 'pointer',
          color: iconColor,
          transition: 'background-color 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
          },
        }}
      >
        {status === 'saving' ? (
          <Box sx={{ position: 'relative', width: 18, height: 18, display: 'inline-flex' }}>
            <Cloud size={18} strokeWidth={1.75} />
            <Loader2 size={10} className="animate-spin" style={{ position: 'absolute', right: -2, bottom: -2 }} />
          </Box>
        ) : status === 'error' ? (
          <CloudOff size={18} strokeWidth={1.75} />
        ) : (
          <CloudCheck size={18} strokeWidth={1.75} />
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              px: 2,
              py: 1.75,
              maxWidth: 280,
              bgcolor: '#1a1a1a',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          {status === 'saving' ? (
            <Cloud
              size={18}
              color="rgba(255, 255, 255, 0.7)"
              strokeWidth={1.75}
              style={{ marginTop: 2, flexShrink: 0 }}
            />
          ) : status === 'error' ? (
            <CloudOff size={18} color={orangeError} strokeWidth={1.75} style={{ marginTop: 2, flexShrink: 0 }} />
          ) : (
            <CloudCheck size={18} color={green01} strokeWidth={1.75} style={{ marginTop: 2, flexShrink: 0 }} />
          )}
          <Box>
            <Typography sx={{ color: '#fff', fontSize: '13px', fontWeight: 600, mb: 0.5 }}>{label}</Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px', lineHeight: 1.45 }}>
              {t('workspace.autosave.popupBody')}
            </Typography>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
